import React, { useState, useEffect } from 'react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import "./index.css";

const ProjectDashboard = () => {
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    guide: 'vijay',
    councillor: '',
    status: '',
    sortBy: 'deadline',
  });
   const [walkins, setWalkins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [guides, setGuides] = useState([]);
  const [councillors, setCouncillors] = useState([]);
  const [showCollegeCounts, setShowCollegeCounts] = useState(false);
  const [collegeFilter, setCollegeFilter] = useState('');
  const api = process.env.REACT_APP_API;

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchWalkins = async () => {
  try {
    const token = localStorage.getItem("jwt_token"); // or 'token' depending on your naming

    const response = await fetch(`${api}/walkins`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` // ✅ Pass token for authentication
      }
    });

    if (response.ok) {
      const data = await response.json();
      setWalkins(data);
      const uniqueGuides = [...new Set(data.map(walkin => walkin.councillorName))];
      setGuides(uniqueGuides.filter(guide => guide));
    } else {
      setError('Failed to fetch walkin data');
    }
  } catch (error) {
    setError('An error occurred while fetching walkin data');
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchWalkins();
  }, [api]);

  const fetchData = async () => {
  try {
    const token = localStorage.getItem("jwt_token"); // or "token" if that's what you stored

    const response = await fetch(`${api}/project-admissions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` // ✅ Include the token
      },
    });

    const result = await response.json();
    setData(result);

    const guideSet = new Set(
      result
        .flatMap(project => [project.guide1, project.guide2])
        .filter(guide => (guide || '').trim() !== '')
    );
    const councillorSet = new Set(result.map(project => project.councillor));

    setGuides([...guideSet]);
    setCouncillors([...councillorSet]);

  } catch (error) {
    console.error('Error fetching data', error);
  }
};

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const totalProjects = data.length;
  // Check for both 'Completed' (from ProjectAdmission) and 'Complete' (from ProjectStatus)
  // Also handle case-insensitive matching
  const completedProjects = data.filter(project => {
    const status = (project.status || '').toString().trim();
    return status.toLowerCase() === 'completed' || status.toLowerCase() === 'complete';
  }).length;
  const onHoldProjects = data.filter(project => {
    const status = (project.status || '').toString().trim();
    console.log('Project status:', status, 'for project:', project.projectId);
    return status.toLowerCase() === 'delivered';
  }).length;
  console.log('Total delivered projects:', onHoldProjects);
  const totalAmountReceived = data.reduce((sum, project) => sum + (Number(project.feesPaid) || 0), 0);
  const totalAmountBalance = data.reduce((sum, project) => {
    const totalFees = Number(project.totalFees) || 0;
    const feesPaid = Number(project.feesPaid) || 0;
    return sum + (totalFees - feesPaid);
  }, 0);
  const totalFeeAmount = data.reduce((sum, project) => sum + (Number(project.totalFees) || 0), 0);  // Total Fees across all projects

  const projectCategories = data.reduce((acc, project) => {
    acc[project.projectCategory] = (acc[project.projectCategory] || 0) + 1;
    return acc;
  }, {});

  const councillorProjects = data.reduce((acc, project) => {
    acc[project.councillor] = (acc[project.councillor] || 0) + 1;
    return acc;
  }, {});

  const guideProjects = data.reduce((acc, project) => {
    if ((project.guide1 || '').trim() !== '') {
      acc[project.guide1] = (acc[project.guide1] || 0) + 1;
    }
    if ((project.guide2 || '').trim() !== '') {
      acc[project.guide2] = (acc[project.guide2] || 0) + 1;
    }
    return acc;
  }, {});

  const councillorAmounts = data.reduce((acc, project) => {
    const { councillor, feesPaid, totalFees } = project;
    if (!acc[councillor]) {
      acc[councillor] = { collected: 0, pending: 0 };
    }
    acc[councillor].collected += feesPaid;
    acc[councillor].pending += (totalFees - feesPaid);
    return acc;
  }, {});

  // ===== NEW FEATURE: Fee Summary for Ishak & Vyshnavi =====
  // Filter projects for only Ishak and Vyshnavi councillors (case-insensitive)
  const specialCouncillors = ['Ishak', 'Vyshnavi'];
  const specialCouncillorData = data.filter(project => {
    if (!project.councillor) return false;
    const councillorLower = project.councillor.trim().toLowerCase();
    return specialCouncillors.some(name => name.toLowerCase() === councillorLower);
  });

  // Calculate fee summary for each special councillor from ProjectAdmission table
  const specialCouncillorSummary = specialCouncillors.reduce((acc, councillorName) => {
    // Case-insensitive matching
    const councillorProjects = specialCouncillorData.filter(project => {
      if (!project.councillor) return false;
      return project.councillor.trim().toLowerCase() === councillorName.toLowerCase();
    });
    
    // Calculate totals from ProjectAdmission table fields: totalFees and feesPaid
    const totalFees = councillorProjects.reduce((sum, project) => 
      sum + (Number(project.totalFees) || 0), 0
    );
    const paidAmount = councillorProjects.reduce((sum, project) => 
      sum + (Number(project.feesPaid) || 0), 0
    );
    const pendingAmount = totalFees - paidAmount;
    const projectCount = councillorProjects.length;

    acc[councillorName] = {
      totalFees,
      paidAmount,
      pendingAmount,
      projectCount // Add project count for verification
    };
    
    return acc;
  }, {});

  // Calculate combined MVP Center total for both councillors
  const mvpCenterTotal = {
    totalFees: Object.values(specialCouncillorSummary).reduce((sum, summary) => sum + summary.totalFees, 0),
    paidAmount: Object.values(specialCouncillorSummary).reduce((sum, summary) => sum + summary.paidAmount, 0),
    pendingAmount: Object.values(specialCouncillorSummary).reduce((sum, summary) => sum + summary.pendingAmount, 0),
    projectCount: Object.values(specialCouncillorSummary).reduce((sum, summary) => sum + summary.projectCount, 0)
  };

  // Calculate totals for remaining councillors (excluding Ishak and Vyshnavi)
  const remainingCouncillorsData = data.filter(project => {
    if (!project.councillor) return false;
    const councillorLower = project.councillor.trim().toLowerCase();
    return !specialCouncillors.some(name => name.toLowerCase() === councillorLower);
  });

  const remainingCouncillorsTotal = {
    totalFees: remainingCouncillorsData.reduce((sum, project) => sum + (Number(project.totalFees) || 0), 0),
    paidAmount: remainingCouncillorsData.reduce((sum, project) => sum + (Number(project.feesPaid) || 0), 0),
    pendingAmount: remainingCouncillorsData.reduce((sum, project) => {
      const totalFees = Number(project.totalFees) || 0;
      const feesPaid = Number(project.feesPaid) || 0;
      return sum + (totalFees - feesPaid);
    }, 0),
    projectCount: remainingCouncillorsData.length
  };

  // Head Office total (same as remaining councillors for now, or can be separate calculation)
  const headOfficeTotal = {
    totalFees: remainingCouncillorsTotal.totalFees,
    paidAmount: remainingCouncillorsTotal.paidAmount,
    pendingAmount: remainingCouncillorsTotal.pendingAmount,
    projectCount: remainingCouncillorsTotal.projectCount
  };
  // ===== END NEW FEATURE =====
  const collegeCount = walkins.reduce((acc, walkin) => {
    if (walkin.status === 'joined') { // Only include students with status 'joined'
      const collegeName = walkin.collegeName;
      if (!acc[collegeName]) {
        acc[collegeName] = 0;
      }
      acc[collegeName]++;
    }
    return acc;
  }, {});
  const filteredCollegeCount = Object.entries(collegeCount).filter(([collegeName]) =>
    collegeName.toLowerCase().includes(collegeFilter.toLowerCase())
  );

  // const toggleCollegeCounts = () => {
  //   setShowCollegeCounts(prev => !prev);
  // };

  return (
    <div className="project-dashboard">
      <div className="project-dashboard__stats">
        <div className="project-dashboard__stat-box">Total Projects: {totalProjects}</div>
        <div className="project-dashboard__stat-box">Completed Projects: {completedProjects}</div>
        <div className="project-dashboard__stat-box">Delivered Projects: {onHoldProjects}</div>
        <div className="project-dashboard__stat-box">Total Paid Amount : ₹{totalAmountReceived}</div>
        <div className="project-dashboard__stat-box">Total Pending Amount : ₹{totalAmountBalance}</div>
        <div className="project-dashboard__stat-box">Total Fee Amount: ₹{totalFeeAmount}</div> {/* New Line */}
      </div>
    {/* MVP Center Combined Total */}
        <div className="project-dashboard__mvp-center-total">
          <h3 className="project-dashboard__mvp-center-title">MVP Center Total</h3>
          <div className="project-dashboard__mvp-center-stats">
            <div className="project-dashboard__mvp-stat-card">
              <div className="mvp-stat-label">Total Fees</div>
              <div className="mvp-stat-value">₹{mvpCenterTotal.totalFees.toLocaleString('en-IN')}</div>
            </div>
            <div className="project-dashboard__mvp-stat-card">
              <div className="mvp-stat-label">Total Collected</div>
              <div className="mvp-stat-value mvp-paid">₹{mvpCenterTotal.paidAmount.toLocaleString('en-IN')}</div>
            </div>
            <div className="project-dashboard__mvp-stat-card">
              <div className="mvp-stat-label">Total Pending</div>
              <div className="mvp-stat-value mvp-pending">₹{mvpCenterTotal.pendingAmount.toLocaleString('en-IN')}</div>
            </div>
            <div className="project-dashboard__mvp-stat-card">
              <div className="mvp-stat-label">Total Projects</div>
              <div className="mvp-stat-value">{mvpCenterTotal.projectCount}</div>
            </div>
          </div>
        </div>

        {/* Remaining Councillors Total */}
      

        {/* Head Office Total */}
        <div className="project-dashboard__head-office-total">
          <h3 className="project-dashboard__head-office-title">Head Office Total</h3>
          <div className="project-dashboard__head-office-stats">
            <div className="project-dashboard__head-office-stat-card">
              <div className="head-office-stat-label">Total Fees</div>
              <div className="head-office-stat-value">₹{headOfficeTotal.totalFees.toLocaleString('en-IN')}</div>
            </div>
            <div className="project-dashboard__head-office-stat-card">
              <div className="head-office-stat-label">Total Collected</div>
              <div className="head-office-stat-value head-office-paid">₹{headOfficeTotal.paidAmount.toLocaleString('en-IN')}</div>
            </div>
            <div className="project-dashboard__head-office-stat-card">
              <div className="head-office-stat-label">Total Pending</div>
              <div className="head-office-stat-value head-office-pending">₹{headOfficeTotal.pendingAmount.toLocaleString('en-IN')}</div>
            </div>
            <div className="project-dashboard__head-office-stat-card">
              <div className="head-office-stat-label">Total Projects</div>
              <div className="head-office-stat-value">{headOfficeTotal.projectCount}</div>
            </div>
          </div>
        </div>
      <div className="project-dashboard__filters">
        {/* Filters section */}
      </div>
      <button onClick={() => setShowCollegeCounts(prev => !prev)}>
        {showCollegeCounts ? 'Hide College Counts' : 'Show College Counts'}
      </button>

      {showCollegeCounts && (
        <div><br/>
          <input
            type="text"
            placeholder="Type college name to filter"
            value={collegeFilter}
            onChange={(e) => setCollegeFilter(e.target.value)}
          />
          <table>
            <thead>
              <tr>
                <th>College Name</th>
                <th>Student Count</th>
              </tr>
            </thead>
            <tbody>
              {filteredCollegeCount.map(([collegeName, count]) => (
                <tr key={collegeName}>
                  <td>{collegeName}</td>
                  <td>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      <div className="project-dashboard__councillor-projects">
        <div className="project-dashboard__bar-graph">
          <Bar
            data={{
              labels: Object.keys(councillorProjects),
              datasets: [
                {
                  label: 'Projects Count',
                  data: Object.values(councillorProjects),
                  backgroundColor: 'rgba(75, 192, 192, 0.6)',
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: true,
                },
                tooltip: {
                  callbacks: {
                    label: function(tooltipItem) {
                      return `${tooltipItem.label}: ${tooltipItem.raw}`;
                    }
                  }
                }
              }
            }}
          />
        </div>

        <div className="project-dashboard__categories">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Project Count</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(projectCategories).map(([category, count]) => (
                <tr key={category}>
                  <td>{category}</td>
                  <td>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="project-dashboard__councillor-amounts">
        <Bar
          data={{
            labels: Object.keys(councillorAmounts),
            datasets: [
              {
                label: 'Collected Amount',
                data: Object.values(councillorAmounts).map(amount => amount.collected),
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
              },
              {
                label: 'Pending Amount',
                data: Object.values(councillorAmounts).map(amount => amount.pending),
                backgroundColor: 'rgba(255, 99, 132, 0.6)',
              },
            ],
          }}
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: true,
              },
              tooltip: {
                callbacks: {
                  label: function(tooltipItem) {
                    return `${tooltipItem.label}: ₹${tooltipItem.raw}`;
                  }
                }
              }
            }
          }}
        />
      </div>
      
      {/* ===== NEW FEATURE: Fee Summary for Ishak & Vyshnavi ===== */}
      <div className="project-dashboard__special-councillor-summary">
       

    
        
        {/* Individual Councillor Cards */}
       

        <div className="project-dashboard__special-summary-content">
          {/* Table Section */}
          

          {/* Optional Bar Chart Section */}
          <div className="project-dashboard__special-summary-chart">
            <Bar
              data={{
                labels: Object.keys(specialCouncillorSummary),
                datasets: [
                  {
                    label: 'Total Fees',
                    data: Object.values(specialCouncillorSummary).map(summary => summary.totalFees),
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                  },
                  {
                    label: 'Paid',
                    data: Object.values(specialCouncillorSummary).map(summary => summary.paidAmount),
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                  },
                  {
                    label: 'Pending',
                    data: Object.values(specialCouncillorSummary).map(summary => summary.pendingAmount),
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: true,
                  },
                  tooltip: {
                    callbacks: {
                      label: function(tooltipItem) {
                        return `${tooltipItem.dataset.label}: ₹${tooltipItem.raw.toLocaleString('en-IN')}`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return '₹' + value.toLocaleString('en-IN');
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
      {/* ===== END NEW FEATURE ===== */}

      <div className="project-dashboard__amounts">
        <Pie
          data={{
            labels: ['Paid', 'Balance'],
            datasets: [
              {
                data: [totalAmountReceived, totalAmountBalance],
                backgroundColor: ['#36A2EB', '#FF6384'],
              },
            ],
          }}
          options={{ maintainAspectRatio: false }}
        />
      </div>

      <div className="project-dashboard__guides">
        <Bar
          data={{
            labels: Object.keys(guideProjects),
            datasets: [
              {
                label: 'Projects Count',
                data: Object.values(guideProjects),
                backgroundColor: 'rgba(153, 102, 255, 0.6)',
              },
            ],
          }}
        />
      </div>

      <div className="project-dashboard__guide-status">
        <div className="project-dashboard__filters">
          <select name="guide" value={filters.guide} onChange={handleFilterChange}>
            <option value="">Select Guide</option>
            {guides.map((guide, index) => (
              <option key={index} value={guide}>{guide}</option>
            ))}
          </select>
        </div>
        <div className="project-dashboard__guide-status-chart">
          <Pie
            data={{
              labels: ['Completed', 'Ongoing', 'Pending', 'Delivered'],
              datasets: [
                {
                  data: [
                    data.filter(project => {
                      const status = (project.status || '').toString().trim().toLowerCase();
                      return project.guide1 === filters.guide && (status === 'completed' || status === 'complete');
                    }).length,
                    data.filter(project => {
                      const status = (project.status || '').toString().trim().toLowerCase();
                      return project.guide1 === filters.guide && status === 'ongoing';
                    }).length,
                    data.filter(project => {
                      const status = (project.status || '').toString().trim().toLowerCase();
                      return project.guide1 === filters.guide && status === 'pending';
                    }).length,
                    data.filter(project => {
                      const status = (project.status || '').toString().trim().toLowerCase();
                      return project.guide1 === filters.guide && status === 'delivered';
                    }).length,
                  ],
                  backgroundColor: ['#4BC0C0', '#FFCE56', '#FF6384', '#FFA500'],
                },
              ],
            }}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
                tooltip: {
                  callbacks: {
                    label: function(tooltipItem) {
                      return `${tooltipItem.label}: ${tooltipItem.raw}`;
                    }
                  }
                }
              }
            }}
          />
        </div>
      </div>

      <div className="project-dashboard__fees-timeline">
        <Line
          data={{
            labels: data.map(project => new Date(project.date).toLocaleDateString()),
            datasets: [
              {
                label: 'Fees Collected Over Time',
                data: data.map(project => project.feesPaid),
                fill: false,
                backgroundColor: '#FF6384',
                borderColor: '#FF6384',
              },
            ],
          }}
        />
      </div>
    </div>
  );
};

export default ProjectDashboard;
